<script setup lang="ts">
import BriefingsSection from '@/components/briefings/BriefingsSection.vue'
import { CHART_ARTICLE_TAG } from '@/constants/content'
import { useGoogleAnalyticsPageView } from '~/composables/google-analytics'
import { useAuthStore } from '~/stores/auth'
import { ResponseStatus } from '~/types/app.type'

const MAX_CHART_ARTICLES = 4
const MAX_VIDEOS = 3

const { $graphqlClient, $seoManager } = useNuxtApp()
const { getBriefings } = useBriefings()
const authStore = useAuthStore()

const isAuthenticated = computed(() => authStore.isAuthenticated)

const fetchHeroData = async () => {
  /*-------------------------------------
    SECTION: HERO
  -------------------------------------*/
  const featuredNews = await $graphqlClient.getFeaturedArticles({
    itemsPerQuery: 4,
  })
  const featuredNewsIds = featuredNews.map((content) => content.id)

  return { featuredNews, featuredNewsIds }
}

const getLatestArticles = async (featuredNewsIds) => {
  /*-------------------------------------
    SECTION: LATEST CONTENT
  -------------------------------------*/
  const { data: latestContents } = await $graphqlClient.getArticlesAndVideos({
    itemsPerQuery: 12,
    excluded: featuredNewsIds,
  })
  const latestContentIds = latestContents.map((content) => content.id)

  return { latestContents: latestContents || [], latestContentIds }
}

const fetchPartTwo = async () => {
  const [
    responseChartArticlesResults,
    responseFeaturedChartArticlesResults,
    responseFeaturedVideoResults,
    responseVideoResults,
  ]: any = await Promise.allSettled([
    $graphqlClient.getArticlesByTags({
      tags: [CHART_ARTICLE_TAG],
      itemsPerQuery: MAX_CHART_ARTICLES,
    }),
    $graphqlClient.getFeaturedArticlesByTags({
      tags: [CHART_ARTICLE_TAG],
      itemsPerQuery: 1,
    }),
    $graphqlClient.getFeaturedVideos({
      itemsPerQuery: MAX_VIDEOS,
    }),
    $graphqlClient.getVideos({
      itemsPerQuery: MAX_VIDEOS,
    }),
  ])

  const chartArticles = responseChartArticlesResults.value || []
  const featuredChartArticles = responseFeaturedChartArticlesResults.value || []
  const featuredVideoResults = responseFeaturedVideoResults.value || []
  const videoResults = responseVideoResults.value.data || []

  /*-------------------------------------
    SECTION: CHARTS
  -------------------------------------*/

  /*-------------------------------------
    SECTION: VIDEOS
  -------------------------------------*/
  let videos = featuredVideoResults

  if (videos.length < 3) {
    const { data: videosData } = videoResults

    videos = videos.concat(videosData.slice(0, MAX_VIDEOS - videos.length))
  } else {
    videos = videos.slice(0, MAX_VIDEOS)
  }

  const videosIds = videos.map((video) => video.id)
  const chartIds = [...chartArticles, ...featuredChartArticles].map(
    (article) => article.id,
  )
  const featuredVideo = videos.shift()

  return {
    chartArticles,
    featuredChartArticles,
    featuredVideo,
    videos,
    chartIds,
    videosIds,
  }
}

const getMoreFromBenchmarkData = async (excluded: string[]) => {
  /*-------------------------------------
    SECTION: MORE FROM BENCHMARK
  -------------------------------------*/
  const { data: moreFromBenchmarkResults } = await $graphqlClient.getArticles({
    itemsPerQuery: 8,
    excluded,
    detailed: false,
  })

  return moreFromBenchmarkResults
}

const { data: heroLatest } = useAsyncData(
  'home-page-hero-latest',
  async () => {
    const { featuredNews, featuredNewsIds } = await fetchHeroData()
    const { latestContents, latestContentIds } =
      await getLatestArticles(featuredNewsIds)

    return { featuredNews, featuredNewsIds, latestContents, latestContentIds }
  },
  {
    default: () => ({
      featuredNews: [],
      featuredNewsIds: [],
      latestContents: [],
      latestContentIds: [],
    }),
  },
)

const { data, status } = useAsyncData(
  'home-page',
  async () => {
    const {
      chartArticles,
      featuredChartArticles,
      featuredVideo,
      videos,
      chartIds,
      videosIds,
    } = await fetchPartTwo()

    return {
      chartArticles,
      featuredChartArticles,
      featuredVideo,
      videos,
      chartIds,
      videosIds,
    }
  },
  {
    server: false,
    default: () => ({
      chartArticles: [],
      featuredChartArticles: [],
      featuredVideo: {},
      videos: [],
      chartIds: [],
      videosIds: [],
    }),
  },
)

const { data: moreFromBenchmarkData, status: moreFromBenchmarkStatus } =
  useAsyncData(
    'home-page-more-from-benchmark',
    async () => {
      const { latestContentIds, featuredNewsIds } = heroLatest.value || {}
      const { chartIds, videosIds } = data.value || {}

      return getMoreFromBenchmarkData([
        ...chartIds,
        ...videosIds,
        ...latestContentIds,
        ...featuredNewsIds,
      ])
    },
    {
      server: false,
      watch: [heroLatest, data],
      default: () => [],
    },
  )

const { data: briefings, status: briefingsStatus } = useAsyncData(
  'home-page-briefings',
  async () => {
    return getBriefings(
      [
        'critical-minerals',
        'batteries',
        'geopolitics',
        'sustainability',
        'new-energy',
      ],
      8,
    )
  },
  {
    server: false,
    watch: [isAuthenticated],
    default: () => [],
  },
)

const moreFromBenchmarkContents = computed(
  () => moreFromBenchmarkData.value || [],
)

const latestContents = computed(() => heroLatest.value?.latestContents || [])
const chartArticles = computed(() => data.value?.chartArticles || [])
const featuredChartArticles = computed(
  () => data.value?.featuredChartArticles || [],
)
const featuredVideo = computed(() => data.value?.featuredVideo || {})
const videos = computed(() => data.value?.videos || [])
const featuredNews = computed(() => heroLatest.value?.featuredNews || [])

const loading = computed(() => status.value !== ResponseStatus.SUCCESS)
const loadingBriefings = computed(
  () => briefingsStatus.value !== ResponseStatus.SUCCESS,
)
const loadingMoreFromBenchmark = computed(
  () => moreFromBenchmarkStatus.value !== ResponseStatus.SUCCESS,
)

const seo = $seoManager.generateSeoInfo({ title: 'Home' })
useHead(seo)
useGoogleAnalyticsPageView(seo.title)
</script>

<template>
  <div class="mb-14 flex grow flex-col md:mb-25">
    <div class="grow">
      <!-- Part One -->
      <section>
        <TheHero :articles="featuredNews" />
      </section>

      <!-- Part Two -->
      <LatestContentSection
        v-if="latestContents.length"
        :contents="latestContents"
      />
      <LoadingSpinner v-if="loading" />
      <section v-else>
        <ChartsSection
          v-if="chartArticles.length"
          :articles="chartArticles"
          :featured-articles="featuredChartArticles"
          :max-articles="MAX_CHART_ARTICLES"
          data-testid="data-visualization-articles"
        />

        <MoreFromBenchmark />

        <VideosSection
          v-if="Object.keys(featuredVideo).length && videos.length"
          :featured-video="featuredVideo"
          :videos="videos"
        />

        <LoadingSpinner v-if="loadingBriefings" />
        <BriefingsSection :briefings="briefings" />

        <!-- Part Three -->

        <LoadingSpinner v-if="loadingMoreFromBenchmark" />
        <MoreFromBenchmarkSection
          v-else-if="moreFromBenchmarkContents.length"
          data-testid="more-from-benchmark"
          :contents="moreFromBenchmarkContents"
        />
      </section>
    </div>
  </div>
</template>

<style scoped>
.show-first-three:nth-child(n + 4) {
  display: none;
}

/*
  The following Tailwind function will make Volar, which
  doesn't fully support that syntax, to show a known
  error.

  See: https://github.com/johnsoncodehk/volar/issues/791
*/
@media (width >= 768px) {
  /* stylelint-disable-next-line selector-class-pattern */
  .md\:show-all:nth-child(n) {
    display: flex;
  }

  /* stylelint-disable-next-line selector-class-pattern */
  .md\:hide-2nd-card-border:nth-child(2) {
    @apply border-none;
  }
}

@media (width >= 1024px) {
  /* stylelint-disable-next-line selector-class-pattern */
  .lg\:show-all:nth-child(n) {
    display: flex;
  }
}
</style>
